import { QUESTION_TYPES, LANGUAGES } from '@/utils/constants'
import { operatorsConfig } from '@/utils/questions'

const { PORTUGUESE, ENGLISH, SPANISH } = LANGUAGES

export const questionsUploadValidations = {
  // functions here receive the single question to evaluate
  singleQuestionValidations: {
    dragAndDropMedia: ({ type, subQuestions, code }) => {
      if (type !== QUESTION_TYPES.dragAndDrop) return

      const hasMedia = subQuestions.some(({ media }) => media)

      if (!hasMedia) return

      const allSubQuestionsHaveMedia = subQuestions.every(({ media }) => media)

      if (allSubQuestionsHaveMedia) return

      return {
        [PORTUGUESE]: `${code}: Em uma pergunta "arrastar e soltar", cada subquestão precisa de uma mídia, se houver`,
        [ENGLISH]: `${code}: On a "Drag and Drop" question, every sub-question needs a media if any has it`,
        [SPANISH]: `${code}: En una pregunta de "arrastrar y soltar", cada subpregunta necesita un medio, si alguno lo tiene`,
      }
    },

    // This is a weak validation for version 1
    // it will be changed to a more robust validation once our next 'condition type' drops
    formulaStructure: ({ type, answers, code }) => {
      if (type !== QUESTION_TYPES.formula) return

      const answersOk = answers.every(({ conditions }) => {
        if (!conditions || !conditions?.length) return false

        const allConditionsOk = conditions.every(
          ({ operator, questionRef, params, type }) => {
            if (!operator || !type || !questionRef.id) return false

            const { inputConfig } = operatorsConfig[operator]

            if (!inputConfig) return params === null

            const inputValuesPresent = inputConfig.every(
              ({ validator, paramProperty }) => {
                const paramValue = params[paramProperty]

                if (!validator) return !!paramValue

                const { isValid } = validator(paramValue)

                return isValid
              },
            )

            return inputValuesPresent
          },
        )

        return allConditionsOk
      })

      if (answersOk) return

      return {
        [PORTUGUESE]: `${code}: Fórmulas incompletas encontradas. Verifique se todos os campos estão selecionados corretamente`,
        [ENGLISH]: `${code}: Incomplete formulas found. Make sure all fields are properly selected`,
        [SPANISH]: `${code}: Se encontraron fórmulas incompletas. Asegúrese de que todos los campos estén seleccionados correctamente`,
      }
    },

    advancedInheritance: ({ advancedOptions, code }) => {
      const { advanced_inheritance, array_filter, array_filter_reverse } =
        advancedOptions || {}

      if (!advanced_inheritance) return

      if (advanced_inheritance && (array_filter || array_filter_reverse))
        return {
          [PORTUGUESE]: `${code}: Herança avançada não pode ser configurada caso haja herança normal configurada`,
          [ENGLISH]: `${code}: Advanced Inheritance cant be present with normal inheritance configuration`,
          [SPANISH]: `${code}: La herencia avanzada no puede estar presente con la configuración de herencia normal`,
        }

      const { origin } = advanced_inheritance

      const order = ['qid', 'code', 'type', 'target', 'alternative']

      const transformed = origin.map(single =>
        order.map(prop => `${single[prop]}`).join(''),
      )

      const duplicateReferences =
        origin.length !== Array.from(new Set(transformed)).length

      if (duplicateReferences)
        return {
          [PORTUGUESE]: `${code}: Origens de herança avançada duplicadas`,
          [ENGLISH]: `${code}: Duplicate advanced inheritance references`,
          [SPANISH]: `${code}: Referencias de herencia avanzada duplicadas`,
        }

      const missingInfo = origin.find(({ qid, code, type, target }) =>
        [qid, code, type, target].some(x => !x),
      )

      if (missingInfo)
        return {
          [PORTUGUESE]: `${code}: Verifique a configuração de herança avançada`,
          [ENGLISH]: `${code}: Advanced Inheritance misconfiguration`,
          [SPANISH]: `${code}: Configuración incorrecta de herencia avanzada`,
        }
    },

    noneOfTheAbove: ({ advancedOptions, answers, code: questionCode }) => {
      const { none_of_the_above } = advancedOptions || {}

      if (!none_of_the_above) return

      const { code, label } = none_of_the_above

      if (!code || !label)
        return {
          [PORTUGUESE]: `${questionCode}: Código ou label vazios em opção avançada`,
          [ENGLISH]: `${questionCode}: None of the above option missing code/label`,
          [SPANISH]: `${questionCode}: Código ou label vazios em opção avançada`,
        }

      const duplicateReferences = answers.find(
        answer => `${answer.code}` === `${code}` || answer.label === label,
      )

      if (duplicateReferences)
        return {
          [PORTUGUESE]: `${questionCode}: Opção avançada possui código ou label duplicada em relação à alternativas`,
          [ENGLISH]: `${questionCode}: None of the above option has duplicate code or label`,
          [SPANISH]: `${questionCode}: Opção avançada possui código ou label duplicada em relação à alternativas`,
        }
    },
  },

  // The functions here will receive the entire survey as parameter
  surveyValidations: {
    tagRandomization: ({ advancedOptions }) => {
      const { randomize_by_tags } = advancedOptions || {}

      if (!randomize_by_tags) return

      const badTag = randomize_by_tags.find(({ name, groups }) => !name || !groups.length)

      if (badTag)
        return {
          [ENGLISH]: 'Bad configuration of randomization tags found',
          [SPANISH]: 'Bad configuration of randomization tags found',
          [PORTUGUESE]: 'Configuração errada de tags de randomização',
        }

      const tagNames = randomize_by_tags.map(({ name }) => name)

      const duplicated = tagNames.find((name, index) => index !== tagNames.indexOf(name))

      if (duplicated)
        return {
          [ENGLISH]: `Duplicated tag name "${duplicated}" found`,
          [SPANISH]: `Duplicated tag name "${duplicated}" found`,
          [PORTUGUESE]: `Tag "${duplicated}" duplicada encontrada`,
        }
    },
  },
}
